import { useEffect, useState } from "react";
import facilityService from "../services/facilityService";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { data } from "autoprefixer";
import { userRoles } from "../helpers/constant";
import { validateUserRole } from "../helpers/helpers";

export default function AddFacility() {
    const { facilityId } = useParams(); // from /editFacility/:facilityId
    const [facility, setFacility] = useState(null);
    const [isEditFacility, setIsEditFacility] = useState(false);

    const [form, setForm] = useState({
        name: "",
        description: "",
        address: "",
        phone_number: "",
        domains: "",
        primary_domain: "",
        timezone: "",
        api_rate_limit: "",
        referral_code: "",
        logo_urls: {
            light_theme_logo: "",
            dark_theme_logo: "",
        },
        intranet_allowed_domains: [""],
        web_search_limit: "30",
        intranet_search_limit: "50",
        project_limit: "10",
        collection_limit: "5",
    });
    let title = isEditFacility ? "Edit Facility" : "Add New Facility";

    useEffect(() => {
        if (facilityId) {
            // editing mode
            setIsEditFacility(true);
            title = "Edit Facility";

            // call your API to fetch facility details
            facilityService.getFacilityById(facilityId)
                .then((facilityData) => {
                    // Normalize logo_urls
                    let logoUrls = facilityData?.logo_urls;
                    if (!logoUrls || Array.isArray(logoUrls)) {
                        logoUrls = {
                            light_theme_logo: "",
                            dark_theme_logo: ""
                        };
                    }

                    let intranetAllowedDomains = facilityData?.intranet_allowed_domains;
                    if (!intranetAllowedDomains || !Array.isArray(intranetAllowedDomains) || intranetAllowedDomains.length === 0) {
                        intranetAllowedDomains = [""];
                    }

                    const normalizedFacility = {
                        ...facilityData,
                        logo_urls: logoUrls,
                        intranet_allowed_domains: intranetAllowedDomains,
                    };

                    setFacility(normalizedFacility);
                    setForm({
                        ...form,
                        ...normalizedFacility, // load data from API
                    });
                })
                .catch((err) => {
                    console.error("Error fetching facility details:", err);
                });
        }
    }, [facilityId]);

    const [errors, setErrors] = useState({});

    const navigate = useNavigate();

    const validateForm = () => {
        const newErrors = {};

        if (!form.name.trim()) newErrors.name = "Name is required.";
        if (!form.description.trim())
            newErrors.description = "Description is required.";
        if (!form.timezone.trim()) newErrors.timezone = "Timezone is required.";
        if (!form.referral_code.trim())
            newErrors.referral_code = "Referral code is required.";

        if (!form.api_rate_limit) {
            newErrors.api_rate_limit = "API Rate Limit is required.";
        } else if (!/^\d+$/.test(form.api_rate_limit)) {
            newErrors.api_rate_limit = "API Rate Limit must be an integer.";
        }

        if (form.web_search_limit) {
            if (!/^\d+$/.test(form.web_search_limit)) {
                newErrors.web_search_limit = "Web Search Limit must be an integer.";
            }
        }
        if (form.intranet_search_limit) {
            if (!/^\d+$/.test(form.intranet_search_limit)) {
                newErrors.intranet_search_limit = "Intranet Search Limit must be an integer.";
            }
        }
        if (form.project_limit) {
            if (!/^\d+$/.test(form.project_limit)) {
                newErrors.project_limit = "Promject Limit must be an integer.";
            }
        }
        if (form.collection_limit) {
            if (!/^\d+$/.test(form.collection_limit)) {
                newErrors.collection_limit = "Collection Limit must be an integer.";
            }
        }

        if (form.phone_number && !/^\d+$/.test(form.phone_number)) {
            newErrors.phone_number = "Phone Number must be numeric.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSave = (formData) => {
        if (!validateForm()) return;
        let data = { ...formData };
        // remove logo_urls if both values are empty
        if (
            data.logo_urls &&
            !data.logo_urls.light_theme_logo &&
            !data.logo_urls.dark_theme_logo
        ) {
            delete data.logo_urls;
        }

        // // remove intranet_allowed_domains if all are empty strings
        if (
            data.intranet_allowed_domains &&
            data.intranet_allowed_domains?.length > 1
        ) {
            data.intranet_allowed_domains = data.intranet_allowed_domains.filter((domain) => domain.trim() !== "");
        }

        if (data.web_search_limit === "") {
            data.web_search_limit = 30;
        }
        if (data.intranet_search_limit === "") {
            data.intranet_search_limit = 50;
        }
        if (data.project_limit === "") {
            data.project_limit = 10;
        }
        if (data.collection_limit === "") {
            data.collection_limit = 5;
        }

        if (isEditFacility) {
            facilityService.updateFacility(facilityId, data)
                .then(() => {
                    toast.success("Facility updated successfully!");
                    navigate(`/facility`);
                })
                .catch((error) => {
                    console.error("Error updating facility:", error);
                });
        } else {

            facilityService.createFacility(data)
                .then(() => {
                    toast.success("Facility created successfully!");
                    navigate(`/facility`);
                })
                .catch((error) => {
                    console.error("Error creating facility:", error);
                });
        }
    };

    // helper to update form & clear error
    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => {
                const updated = { ...prev };
                delete updated[field];
                return updated;
            });
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-10">
            <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg p-8">
                {/* Header */}
                <div className="mb-6 border-b pb-4">
                    <h2 className="text-2xl font-semibold text-gray-800">{title}</h2>
                </div>

                {/* Grid Inputs - 3 per row */}
                <div className="grid grid-cols-3 gap-4">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => handleChange("name", e.target.value)}
                            className={`w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 ${errors.name
                                ? "border-red-500 focus:ring-red-400"
                                : "focus:ring-purple-400"
                                }`}
                        />
                        {errors.name && (
                            <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                        )}
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                            className={`w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 ${errors.description
                                ? "border-red-500 focus:ring-red-400"
                                : "focus:ring-purple-400"
                                }`}
                        />
                        {errors.description && (
                            <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                        )}
                    </div>

                    {/* Address */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Address
                        </label>
                        <input
                            type="text"
                            value={form.address}
                            onChange={(e) => handleChange("address", e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />
                    </div>

                    {/* Phone Number */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number
                        </label>
                        <input
                            type="text"
                            value={form.phone_number}
                            placeholder="Enter valid phone number"
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                handleChange("phone_number", value);
                            }}
                            className={`w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 ${errors.phone_number
                                ? "border-red-500 focus:ring-red-400"
                                : "focus:ring-purple-400"
                                }`}
                        />
                        {errors.phone_number && (
                            <p className="text-red-500 text-sm mt-1">{errors.phone_number}</p>
                        )}
                    </div>

                    {/* Domains */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Domains
                        </label>
                        <input
                            type="text"
                            value={form.domains}
                            onChange={(e) => handleChange("domains", e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />
                    </div>

                    {/* Primary Domain */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Primary Domain
                        </label>
                        <input
                            type="text"
                            value={form.primary_domain}
                            onChange={(e) => handleChange("primary_domain", e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />
                    </div>

                    {/* Timezone */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Time Zone <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="America/New_York"
                            value={form.timezone}
                            onChange={(e) => handleChange("timezone", e.target.value)}
                            className={`w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 ${errors.timezone
                                ? "border-red-500 focus:ring-red-400"
                                : "focus:ring-purple-400"
                                }`}
                        />
                        {errors.timezone && (
                            <p className="text-red-500 text-sm mt-1">{errors.timezone}</p>
                        )}
                    </div>

                    {/* API Rate Limit */}
                    <div className="relative overflow-visible">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            API Rate Limit
                            <span className="text-red-500">*</span>
                            <span className="relative inline-block ml-1 group">
                                <span
                                    className="text-xs align-top cursor-default text-gray-400 hover:text-purple-600 font-bold"
                                >
                                    ?
                                </span>
                                <div
                                    className="absolute left-1/2 transform -translate-x-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg pointer-events-none"
                                    style={{ top: '0', transform: 'translate(-50%, calc(-100% - 8px))' }}
                                >
                                    Maximum number of API requests Per Day For each Model.
                                </div>
                            </span>

                        </label>
                        <input
                            type="text"
                            value={form.api_rate_limit}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                handleChange("api_rate_limit", value);
                            }}
                            className={`w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 ${errors.api_rate_limit
                                ? "border-red-500 focus:ring-red-400"
                                : "focus:ring-purple-400"
                                }`}
                            placeholder="Enter digits only"
                        />
                        {errors.api_rate_limit && (
                            <p className="text-red-500 text-sm mt-1">{errors.api_rate_limit}</p>
                        )}
                    </div>

                    {/* Websearch Limit */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Websearch Limit
                            <span className="relative inline-block ml-1 group">
                                <span
                                    className="text-xs align-top cursor-default text-gray-400 hover:text-purple-600 font-bold"
                                >
                                    ?
                                </span>
                                <div
                                    className="absolute left-1/2 transform -translate-x-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg pointer-events-none"
                                    style={{ top: '0', transform: 'translate(-50%, calc(-100% - 8px))' }}
                                >
                                    Maximum number of Websearch requests Per Day For each User.
                                </div>
                            </span>
                        </label>
                        <input
                            type="text"
                            value={form.web_search_limit}
                            readOnly={!validateUserRole(userRoles.SUPERADMIN)}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                handleChange("web_search_limit", value);
                            }}
                            className={`w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 ${errors.web_search_limit
                                ? "border-red-500 focus:ring-red-400"
                                : "focus:ring-purple-400"
                                }`}
                            placeholder="Enter digits only"
                        />
                        {errors.web_search_limit && (
                            <p className="text-red-500 text-sm mt-1">{errors.web_search_limit}</p>
                        )}
                    </div>

                    {/* intranet Limit */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Intranet Search Limit
                            <span className="relative inline-block ml-1 group">
                                <span
                                    className="text-xs align-top cursor-default text-gray-400 hover:text-purple-600 font-bold"
                                >
                                    ?
                                </span>
                                <div
                                    className="absolute left-1/2 transform -translate-x-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg pointer-events-none"
                                    style={{ top: '0', transform: 'translate(-50%, calc(-100% - 8px))' }}
                                >
                                    Maximum number of Intranet Search requests Per Day For each User.
                                </div>
                            </span>
                        </label>
                        <input
                            type="text"
                            value={form.intranet_search_limit}
                            readOnly={!validateUserRole(userRoles.SUPERADMIN)}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                handleChange("intranet_search_limit", value);
                            }}
                            className={`w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 ${errors.intranet_search_limit
                                ? "border-red-500 focus:ring-red-400"
                                : "focus:ring-purple-400"
                                }`}
                            placeholder="Enter digits only"
                        />
                        {errors.intranet_search_limit && (
                            <p className="text-red-500 text-sm mt-1">{errors.intranet_search_limit}</p>
                        )}
                    </div>

                    {/* Project Limit */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Project Limit
                            <span className="relative inline-block ml-1 group">
                                <span
                                    className="text-xs align-top cursor-default text-gray-400 hover:text-purple-600 font-bold"
                                >
                                    ?
                                </span>
                                <div
                                    className="absolute left-1/2 transform -translate-x-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg pointer-events-none"
                                    style={{ top: '0', transform: 'translate(-50%, calc(-100% - 8px))' }}
                                >
                                    Maximum number of Projects each User can create.
                                </div>
                            </span>
                        </label>
                        <input
                            type="text"
                            value={form.project_limit}
                            readOnly={!validateUserRole(userRoles.SUPERADMIN)}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                handleChange("project_limit", value);
                            }}
                            className={`w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 ${errors.project_limit
                                ? "border-red-500 focus:ring-red-400"
                                : "focus:ring-purple-400"
                                }`}
                            placeholder="Enter digits only"
                        />
                        {errors.project_limit && (
                            <p className="text-red-500 text-sm mt-1">{errors.project_limit}</p>
                        )}
                    </div>

                    {/* Collection Limit */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Collection Limit
                            <span className="relative inline-block ml-1 group">
                                <span
                                    className="text-xs align-top cursor-default text-gray-400 hover:text-purple-600 font-bold"
                                >
                                    ?
                                </span>
                                <div
                                    className="absolute left-1/2 transform -translate-x-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg pointer-events-none"
                                    style={{ top: '0', transform: 'translate(-50%, calc(-100% - 8px))' }}
                                >
                                    Maximum number of Collections each User can create.
                                </div>
                            </span>
                        </label>
                        <input
                            type="text"
                            value={form.collection_limit}
                            readOnly={!validateUserRole(userRoles.SUPERADMIN)}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                handleChange("collection_limit", value);
                            }}
                            className={`w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 ${errors.collection_limit
                                ? "border-red-500 focus:ring-red-400"
                                : "focus:ring-purple-400"
                                }`}
                            placeholder="Enter digits only"
                        />
                        {errors.collection_limit && (
                            <p className="text-red-500 text-sm mt-1">{errors.collection_limit}</p>
                        )}
                    </div>

                    {/* Referral Code */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Referral Code <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.referral_code}
                            onChange={(e) => {
                                let value = e.target.value
                                    .toUpperCase()
                                    .replace(/[^A-Z0-9]/g, "");
                                if (value.length > 4) {
                                    value = value.slice(0, 4) + "-" + value.slice(4);
                                }
                                handleChange("referral_code", value);
                            }}
                            maxLength={9}
                            placeholder="ABCD-2025"
                            className={`w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 ${errors.referral_code
                                ? "border-red-500 focus:ring-red-400"
                                : "focus:ring-purple-400"
                                }`}
                        />
                        {errors.referral_code && (
                            <p className="text-red-500 text-sm mt-1">{errors.referral_code}</p>
                        )}
                    </div>
                </div>

                {/* Logo URLs (kept same style) */}
                <div className="mt-6 mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Logo URLs</label>
                    <div className="grid grid-cols-1 gap-4">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                placeholder="Light theme logo url"
                                value={form.logo_urls.light_theme_logo}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        logo_urls: { ...form.logo_urls, light_theme_logo: e.target.value },
                                    })
                                }
                                className="flex-1 px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                            <input
                                type="text"
                                placeholder="Dark theme logo url"
                                value={form.logo_urls.dark_theme_logo}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        logo_urls: { ...form.logo_urls, dark_theme_logo: e.target.value },
                                    })
                                }
                                className="flex-1 px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                        </div>
                        <input
                            type="text"
                            placeholder="Icon url"
                            value={form.logo_urls.desktop_app_logo}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    logo_urls: { ...form.logo_urls, desktop_app_logo: e.target.value },
                                })
                            }
                            className="w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />
                    </div>
                </div>

                {/* Intranet Allowed Domains (kept same dynamic UI) */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Intranet Allowed Domains
                    </label>

                    {form.intranet_allowed_domains.map((domain, idx) => (
                        <div key={idx} className="flex items-center gap-2 mb-2">
                            <input
                                type="text"
                                value={domain}
                                onChange={(e) => {
                                    const updated = [...form.intranet_allowed_domains];
                                    updated[idx] = e.target.value;
                                    setForm({ ...form, intranet_allowed_domains: updated });
                                }}
                                className="flex-1 px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />

                            {/* Minus button (only show if more than 1 input) */}
                            {form.intranet_allowed_domains.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const updated = form.intranet_allowed_domains.filter((_, i) => i !== idx);
                                        setForm({ ...form, intranet_allowed_domains: updated });
                                    }}
                                    className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600"
                                >
                                    –
                                </button>
                            )}

                            {/* Plus button (only show on last input) */}
                            {idx === form.intranet_allowed_domains.length - 1 && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        setForm({
                                            ...form,
                                            intranet_allowed_domains: [...form.intranet_allowed_domains, ""],
                                        })
                                    }
                                    className="p-2 rounded-full bg-purple-500 text-white hover:bg-purple-600"
                                >
                                    +
                                </button>
                            )}
                        </div>
                    ))}
                </div>


                {/* Actions */}
                <div className="flex justify-end space-x-3 mt-6">
                    <button
                        onClick={() => onSave(form)}
                        className="px-6 py-2 rounded-full bg-gradient-to-r from-purple-400 to-purple-500 text-white font-semibold shadow hover:opacity-95 transition"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}
